/**
 * Persistence for generated job descriptions.
 *
 * Saved per (profile, solicitation, role) so a JD can be recalled and re-edited
 * instead of regenerated. RLS scopes rows to the owning profile (auth.uid()).
 */

import { supabase } from '../lib/supabase';
import type { JobDescription } from '../types/job-description';

/** Stable per-role key within a solicitation (normalized title). */
export function roleKeyFromTitle(title: string): string {
  return (title || 'role')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'role';
}

/** Stable solicitation key for a contract row. */
export function contractKey(contract: any): string {
  return String(contract?.notice_id || contract?.id || '').trim();
}

export async function loadSavedRoleKeys(profileId: string, noticeId: string): Promise<Set<string>> {
  if (!profileId || !noticeId) return new Set();
  const { data, error } = await supabase
    .from('job_descriptions')
    .select('role_key')
    .eq('profile_id', profileId)
    .eq('notice_id', noticeId);
  if (error) {
    console.error('loadSavedRoleKeys failed', error);
    return new Set();
  }
  return new Set((data || []).map((r: any) => r.role_key));
}

export async function loadSavedJobDescription(
  profileId: string,
  noticeId: string,
  roleKey: string
): Promise<JobDescription | null> {
  if (!profileId || !noticeId || !roleKey) return null;
  const { data, error } = await supabase
    .from('job_descriptions')
    .select('content_json')
    .eq('profile_id', profileId)
    .eq('notice_id', noticeId)
    .eq('role_key', roleKey)
    .maybeSingle();
  if (error) {
    console.error('loadSavedJobDescription failed', error);
    return null;
  }
  return (data?.content_json as JobDescription) || null;
}

export async function saveJobDescription(args: {
  profileId: string;
  noticeId: string;
  roleKey: string;
  roleTitle: string;
  jd: JobDescription;
}): Promise<void> {
  const { profileId, noticeId, roleKey, roleTitle, jd } = args;
  if (!profileId || !noticeId) throw new Error('Cannot save: missing profile or solicitation.');
  const { error } = await supabase
    .from('job_descriptions')
    .upsert(
      {
        profile_id: profileId,
        notice_id: noticeId,
        role_key: roleKey,
        role_title: roleTitle,
        content_json: jd,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'profile_id,notice_id,role_key' }
    );
  if (error) throw new Error(error.message || 'Failed to save job description');
}
